import { LeakData } from './leak.model'
import { GROUPS } from 'util/enums/userGroup'
import { AdminStatusType } from './admin.model'
import { RiskLevel } from 'constants/risk-level'

export enum DeepScanStatus {
  NOT_STARTED = 'NOT_STARTED',
  INVITED = 'INVITED',
  NOT_INVITED = 'NOT_INVITED',
  INITIATED = 'INITIATED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  PROCESSING = 'PROCESSING',
  SCANNING = 'SCANNING',
  UNAVAILABLE_SEATS = 'UNAVAILABLE_SEATS',
  IMPORTANT = 'IMPORTANT',
  CRITICAL = 'CRITICAL',
}

export type StatusType =
  | 'COMPLETED'
  | 'NOT_STARTED'
  | 'INITIATED'
  | 'INVITED'
  | 'NOT_INVITED'
  | 'FAILED'
  | 'PROCESSING'
  | 'SCANNING'
  | 'UNAVAILABLE_SEATS'
  | 'IMPORTANT'
  | 'CRITICAL'

export enum EUserRole {
  ADMIN = 'admin',
  USER_B2C = 'userB2C',
  EMPLOYEE = 'employee',
}

export type TUserRole = 'admin' | 'userB2C' | 'employee'
export type TGroups = (typeof GROUPS)[keyof typeof GROUPS]

export enum EUserStatus {
  ADMIN = 'admin',
  USER_B2C = 'userB2C',
  EMPLOYEE = 'employee',
}

export type EUserInvited = {
  name: string
  email: string
  cognitoId: string
  role: string
  uid: string
  state: string
}

export type TCognitoUserAdmin = {
  name: string
  email: string
  dateCreated: string
}
export type TUser = {
  name: string
  phone: string
  email: string
  email_verified: boolean
  companyId: string
  groups?: TGroups[]
  role?: string
}
export type TSignUpForm = {
  fullname: string
  alternativeNames: Array<string> | null
  haveCyberSecurityInsurance: boolean | null
  phoneNumber: string
  company?: string
  email: string
  secondaryEmails: Array<string> | null
  gender: string
  birthDate: string | Date
  address: {
    street: string
    number: string
    postalCode: string
    city: string
    country: string
    countryState: string
  }
  havePasswordManager: boolean | null
  useMFA: boolean | null
  haveResponsePlan: boolean | null
  image: File | null | string
  top10Apps: Array<string>
  socialMediaAccounts: {
    facebook: string | null
    tiktok: string | null
    twitter: string | null
    instagram: string | null
  }
}

export interface IUserScan {
  personal_emails?: string[]
  gender?: string
  social_media: {
    linkedin?: string
    facebook?: string
    twitter?: string
  }
  address: {
    street?: string
    street_complement?: string
    postal_code?: string
  }
}

export interface IUserReport {
  id: string
  url: string
  context: string
  leakType: 'email' | 'address' | 'phone' | 'domain' | 'image' | 'name'
  registerId: string
  createdAt: string
}

export interface IUserRow {
  id: string
  fullname: string | null
  email: string | null
  department:
    | string
    | {
        _id?: string | undefined
        name?: string | undefined
      }
    | null
  role: string | null
  deepScanStatus: DeepScanStatus | null
  deactivatedBy: string | { _id?: string; fullname?: string; email?: string } | null
  deactivatedAt: Date | null
  findingsCount: number
  departmentName: string | null
  riskLevels: { riskLevel: RiskLevel; count: number }[]
  isAdmin: boolean
  isOwner: boolean
}

export interface IUserTableData {
  data: IUserRow[]
  pagination: {
    currentPage: number
    totalRecords: number
    totalPages: number
    recordsPerPage: number
  }
}

export interface IUserDetailed {
  mfa: boolean
  useMFA: boolean | null
  fullname: string
  role?: string | null
  caption: string
  createdAt?: string | null
  cybersecurityResponsePlan: boolean
  haveResponsePlan: boolean | null
  havePasswordManager: boolean | null
  training: string
  email: string
  phoneNumber: string | null
  location: string
  deepScanStatus: DeepScanStatus
  recommendations: Recommendation[]
  isScanning: boolean
  userProtexxaScore: number | null
  protexxaScoreDelta: number | null
  companyScanLastDate: Date | string | null
  deepScan: {
    lastScan: string | null
    nextScheduledScan: string | null
    totalExposures: number
    takedownRequests: number
    passwordCompromises: number
    emailExposures: number
    addressExposures: number
    nameExposures: number
    imageExposures: number
  }
  exposuresTable?: {
    id: string
    registerId: string
    fullname: string
    email: string
    caption: string
    source: string
    breach: LeakData | null
    issue: string
    threatLevel: string
    dateOfExposure: Date | string
    dateBreached: Date | string
    deepScanStatus: string
    riskLevel: RiskLevel | null
    defaultRiskLevel: RiskLevel | null
  }[]
  findingsData: {
    total: number
    findings: {
      riskLevel: RiskLevel
      total: number
    }[]
  }
}

type Recommendation = {
  id: string
  title: string
  description: string
  actionType: RecommendationActionType
  priority: 'high' | 'medium' | 'low'
}

export enum RecommendationCode {
  VERIFY_DATA_CONSENT = 'verify-data-consent',
  RUN_DEEP_SCAN = 'run-deep-scan',
  CHECK_USER_PROFILE = 'check-user-profile',
  RESET_LEAKED_PASSWORDS = 'reset-leaked-passwords',
  CHECK_SIMILAR_PASSWORDS = 'check-similar-passwords',
}

enum RecommendationActionType {
  ALERT_USER = 'alert_user',
  PROMPT_DEEP_SCAN = 'prompt_deep_scan',
  VIEW_PROFILE = 'view_profile',
  CHANGE_PASSWORD = 'change_password',
  CHECK_FINDINGS = 'check_findings',
}

export const isRecommendationCode = (value: string): value is RecommendationCode => {
  return Object.values(RecommendationCode).includes(value as RecommendationCode)
}

export type GetAllUsersForBulkNotifyOutputDto = {
  id: string
  name: string
  email?: string
  emails?: string[]
  selected: boolean
}

export type GetAllUsersForBulkNotifyInitialOutputDto = {
  id: string
  name: string
  email?: string
  emails?: string[]
  status?: AdminStatusType
}

export type GetAllUsersForBulkNotifyInputDto = {
  search: string
  companyId: string
  filter: string
}

export type GetAllUsersCountForBulkNotifyOutputDto = {
  totalCount: number
}

export type GetAllUsersCountForBulkNotifyInputDto = {
  companyId: string
  filter: string
}

export enum EUserGroup {
  USERS = 'users',
  DEPARTMENTS = 'departments',
  EXECUTIVES = 'executives',
}

export const enum LeakCategory {
  BUSINESS = 'business',
  PERSONAL = 'personal',
}

export const enum FindingsType {
  ALL = 'all',
  EXTERNAL = 'external',
  INTERNAL = 'internal',
}
